import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SplashScreen } from '../components/splash-screen';
import { SettingsConsumer, SettingsProvider } from '../contexts/settings-context';
import { AuthConsumer, AuthProvider } from '../contexts/jwt-context';
import { gtmConfig } from '../config';
import { gtm } from '../lib/gtm';
import { createTheme } from '../theme';
import { createEmotionCache } from '../utils/create-emotion-cache';
import { useRoutes } from 'react-router-dom';
import routes from '../modules/routes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const clientSideEmotionCache = createEmotionCache();

// QueryClient global : les données sont mises en cache entre les navigations
// staleTime   : données considérées fraîches pendant 3 min → pas de re-fetch inutile
// gcTime      : données gardées en mémoire 10 min après la dernière utilisation
// retry       : un seul retry en cas d'échec réseau (pas de boucle infinie)
// refetchOnWindowFocus : désactivé pour éviter des re-fetch surprises
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const content = useRoutes(routes);

  useEffect(() => {
    gtm.initialize(gtmConfig);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={emotionCache}>
        <AuthProvider>
          <SettingsProvider>
            <SettingsConsumer>
              {({ settings }) => (
                <ThemeProvider
                  theme={createTheme({
                    direction: settings.direction,
                    responsiveFontSizes: settings.responsiveFontSizes,
                    mode: settings.theme
                  })}
                >
                  <CssBaseline />
                  <Toaster position="top-center" />
                  <AuthConsumer>
                    {(auth) => !auth.isInitialized
                      ? <SplashScreen />
                      : content}
                  </AuthConsumer>
                </ThemeProvider>
              )}
            </SettingsConsumer>
          </SettingsProvider>
        </AuthProvider>
      </CacheProvider>
    </QueryClientProvider>
  );
};

export default App;
